import { X, Trash2 } from 'lucide-react';
import Avatar from '../ui/Avatar';
import Badge from '../ui/Badge';
import { TEAMS } from '../../data/teams';
const CHANNELS = [
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'web', label: 'Web' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'email', label: 'E-mail' },
  { id: 'outros', label: 'Outros' },
];

export default function RightProfilePanel({
  conversation,
  contact,
  companies = [],
  tags = [],
  funnelStages = [],
  teams: teamsProp = {},
  onUpdateConversation,
  onUpdateContact,
  onClose,
  onDeleteConversation,
  isAdmin,
}) {
  if (!conversation) return null;

  const name = conversation.name || contact?.nome || '—';
  const email = conversation.email || contact?.email || '—';
  const phone = conversation.phone || contact?.telefone || '—';
  const companyName = contact?.empresa_id && companies.length
    ? (companies.find((c) => c.id === contact.empresa_id)?.nome_fantasia || '—')
    : null;
  const canalLabel = contact?.canal_origem
    ? (CHANNELS.find((ch) => ch.id === contact.canal_origem)?.label || contact.canal_origem)
    : null;
  const contactTagIds = contact?.tags && Array.isArray(contact.tags) ? contact.tags : [];
  const tagsList = Array.isArray(tags) ? tags : [];
  const contactTags = contactTagIds
    .filter((id) => id != null)
    .map((tagId) => tagsList.find((t) => t && (t.id === tagId || String(t.id) === String(tagId))))
    .filter(Boolean);
  const observacoes = contact?.observacoes_internas?.trim() || null;
  const contactStatus = contact?.status ?? 'ativo';
  const teamsList = Object.values(teamsProp && typeof teamsProp === 'object' ? teamsProp : {}).length
    ? Object.values(teamsProp)
    : Object.values(TEAMS || {});
  const teamId = conversation.teamId?.toUpperCase();
  const team = (teamId && (teamsProp?.[teamId] || TEAMS?.[teamId])) ? (teamsProp?.[teamId] || TEAMS?.[teamId]) : null;
  const stage = conversation.stage ?? 'triagem';
  const stageValue = (Array.isArray(funnelStages) ? funnelStages.find((s) => s && (s.id === stage || s.slug === stage)) : null)?.slug || stage;

  const handleStageChange = (e) => {
    const value = e.target.value;
    if (value) onUpdateConversation?.(conversation.id, { stage: value });
  };

  const handleTeamChange = (e) => {
    const value = e.target.value || null;
    onUpdateConversation?.(conversation.id, { teamId: value || null });
  };

  const handleContactStatusChange = (e) => {
    const value = e.target.value;
    if (contact?.id) onUpdateContact?.(contact.id, { status: value });
  };

  const handleTagToggle = (tagId, checked) => {
    try {
      if (!contact?.id || !onUpdateContact) return;
      const idStr = String(tagId);
      const current = (contact.tags && Array.isArray(contact.tags) ? contact.tags : [])
        .filter((id) => id != null)
        .map((id) => String(id));
      const next = checked
        ? (current.includes(idStr) ? current : [...current, idStr])
        : current.filter((id) => id !== idStr);
      onUpdateContact(contact.id, { tags: next });
    } catch (err) {
      console.error('Erro ao alterar etiqueta', err);
    }
  };

  return (
    <div className="w-80 bg-appSurface border-l border-appSurfaceBorder p-6 flex flex-col animate-in slide-in-from-right duration-300 overflow-y-auto max-h-full">
      <div className="flex justify-between w-full mb-6">
        <h3 className="font-bold text-appText">Perfil do contato</h3>
        <button type="button" onClick={onClose} aria-label="Fechar"><X size={20}/></button>
      </div>
      <Avatar src={conversation.avatarUrl} label={conversation.avatarLabel ?? conversation.avatar} size="xl" color="bg-appSurfaceBorder/50" />
      <h2 className="mt-4 text-xl font-bold text-appText">{name}</h2>
      {email !== '—' && <p className="text-appTextMuted text-sm">{email}</p>}
      <div className="w-full space-y-3 mt-6">
        <div className="p-4 bg-appSurfaceBorder/20 rounded-2xl">
          <p className="text-[10px] font-black text-appTextMuted uppercase tracking-widest mb-1">Telefone</p>
          <p className="text-sm font-medium text-appText">{phone}</p>
        </div>

        {/* Etapa do funil — sincronizada com a página do funil */}
        {funnelStages?.length > 0 && (
          <div className="p-4 bg-appSurfaceBorder/20 rounded-2xl">
            <p className="text-[10px] font-black text-appTextMuted uppercase tracking-widest mb-1">Etapa</p>
            {onUpdateConversation ? (
              <select
                value={stageValue}
                onChange={handleStageChange}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-appSurfaceBorder bg-appSurface text-appText text-sm font-medium focus:ring-2 focus:ring-appPrimary focus:border-appPrimary outline-none"
              >
                {funnelStages.map((s) => (
                  <option key={s.id} value={s.slug || s.id}>{s.label}</option>
                ))}
              </select>
            ) : (
              <p className="text-sm font-medium text-appText">
                {funnelStages.find((s) => s.id === stage || s.slug === stage)?.label ?? stage}
              </p>
            )}
          </div>
        )}

        {/* Equipe */}
        <div className="p-4 bg-appSurfaceBorder/20 rounded-2xl">
          <p className="text-[10px] font-black text-appTextMuted uppercase tracking-widest mb-1">Equipe</p>
          {onUpdateConversation && teamsList.length > 0 ? (
            <select
              value={conversation.teamId ?? ''}
              onChange={handleTeamChange}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-appSurfaceBorder bg-appSurface text-appText text-sm font-medium focus:ring-2 focus:ring-appPrimary focus:border-appPrimary outline-none"
            >
              <option value="">Nenhuma</option>
              {teamsList.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          ) : team ? (
            <Badge colorClass={team.color}>{team.name}</Badge>
          ) : (
            <p className="text-sm text-appTextMuted">—</p>
          )}
        </div>

        {companyName != null && (
          <div className="p-4 bg-appSurfaceBorder/20 rounded-2xl">
            <p className="text-[10px] font-black text-appTextMuted uppercase tracking-widest mb-1">Empresa</p>
            <p className="text-sm font-medium text-appText">{companyName}</p>
          </div>
        )}
        {canalLabel && (
          <div className="p-4 bg-appSurfaceBorder/20 rounded-2xl">
            <p className="text-[10px] font-black text-appTextMuted uppercase tracking-widest mb-1">Canal de origem</p>
            <p className="text-sm font-medium text-appText">{canalLabel}</p>
          </div>
        )}

        {/* Etiquetas (tags) — vinculadas ao contato, refletem em todo o sistema */}
        <div className="p-4 bg-appSurfaceBorder/20 rounded-2xl">
          <p className="text-[10px] font-black text-appTextMuted uppercase tracking-widest mb-2">Etiquetas</p>
          {onUpdateContact && contact?.id && tagsList.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {tagsList.filter((t) => t && t.id != null).map((t) => {
                const tagId = String(t.id);
                const isChecked = contactTagIds.some((id) => id != null && String(id) === tagId);
                return (
                  <div
                    key={t.id}
                    role="button"
                    tabIndex={0}
                    className="flex items-center gap-2 cursor-pointer select-none hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-appPrimary focus:ring-offset-1 rounded"
                    onClick={() => handleTagToggle(t.id, !isChecked)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleTagToggle(t.id, !isChecked);
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      readOnly
                      tabIndex={-1}
                      className="rounded border-appSurfaceBorder text-appPrimary focus:ring-appPrimary pointer-events-none"
                    />
                    <Badge colorClass={t.color || 'bg-appSurfaceBorder text-appText'}>{t.name}</Badge>
                  </div>
                );
              })}
            </div>
          ) : contactTags.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {contactTags.map((t) => (
                <Badge key={t.id} colorClass={t.color || 'bg-appSurfaceBorder text-appText'}>{t.name}</Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-appTextMuted">Nenhuma etiqueta</p>
          )}
        </div>

        {observacoes && (
          <div className="p-4 bg-appSurfaceBorder/20 rounded-2xl">
            <p className="text-[10px] font-black text-appTextMuted uppercase tracking-widest mb-1">Observações internas</p>
            <p className="text-sm font-medium text-appText whitespace-pre-wrap">{observacoes}</p>
          </div>
        )}

        {/* Status do contato (Ativo / Inativo) */}
        <div className="p-4 bg-appSurfaceBorder/20 rounded-2xl">
          <p className="text-[10px] font-black text-appTextMuted uppercase tracking-widest mb-1">Status do contato</p>
          {onUpdateContact && contact ? (
            <select
              value={contactStatus}
              onChange={handleContactStatusChange}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-appSurfaceBorder bg-appSurface text-appText text-sm font-medium focus:ring-2 focus:ring-appPrimary focus:border-appPrimary outline-none"
            >
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
            </select>
          ) : (
            <p className="text-sm font-medium text-appText">{contactStatus === 'ativo' ? 'Ativo' : 'Inativo'}</p>
          )}
        </div>

        {/* Excluir conversa — apenas administrador */}
        {isAdmin && onDeleteConversation && conversation?.id && (
          <div className="mt-4 pt-4 border-t border-appSurfaceBorder">
            <button
              type="button"
              onClick={() => window.confirm('Excluir esta conversa? Esta ação não pode ser desfeita.') && onDeleteConversation(conversation.id)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-red-500/50 text-red-600 hover:bg-red-500/10 text-sm font-medium"
            >
              <Trash2 size={16} />
              Excluir conversa
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
